import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { SplitReveal } from '@/components/motion';
import { useReducedMotion } from '@/hooks/use-reduced-motion';
import { gsap, useGSAP } from '@/lib/gsap';
import { DURATION, GSAP_EASE } from '@/lib/motion-tokens';
import { cn } from '@ibas/ui';

/** A single campus slide shown on the stage. */
interface CampusSlide {
  /** Site-relative path to the campus photo under /media. */
  image: string;
  /** Meaningful alt describing a campus/study scene — never an institutional claim. */
  alt: string;
  /** Short caption overlaid on the stage. */
  caption: string;
  /** CSS object-position keeping the subject in frame under object-cover. */
  position: string;
}

/**
 * Real IBAS campus photography from the site media library.
 * Each slide carries scene-level alt text only — no institutional
 * facts, statistics or claims are asserted about the imagery.
 */
const SLIDES: readonly CampusSlide[] = [
  {
    image: '/media/images/campus/campus-building-exterior.webp',
    alt: 'Exterior view of the modern copper-clad IBAS campus building with a landscaped entrance.',
    caption: 'The Thalwil campus building',
    // Tall portrait in a wide stage — hold the facade rather than the sky.
    position: '50% 45%',
  },
  {
    image: '/media/images/campus/campus-classroom-tables.webp',
    alt: 'Empty IBAS classroom with rows of tables and chairs beside large windows.',
    caption: 'Modern lecture halls',
    // Bias down so the tables and chairs read, not just the ceiling.
    position: '50% 55%',
  },
  {
    image: '/media/images/campus/campus-lounge-sofas.webp',
    alt: 'Lounge area with grey sofas and orange cushions overlooking large windows at IBAS.',
    caption: 'Student lounge spaces',
    // Balance the bright windows above with the sofas below.
    position: '50% 45%',
  },
  {
    image: '/media/images/campus/campus-lake-town-view.webp',
    alt: 'View over a lakeside Swiss town near the IBAS campus.',
    caption: 'Lake Zurich, minutes away',
    // Keep the lake and town on the horizon in frame.
    position: '50% 45%',
  },
  {
    image: '/media/images/campus/campus-dining-lounge-room.webp',
    alt: 'Dining and lounge area with sofas and a table set for a meal at the IBAS building.',
    caption: 'Dining & social spaces',
    // Bias down to keep the laid dining table in frame.
    position: '50% 55%',
  },
];

const AUTO_ADVANCE_MS = 6000;

export interface CampusSliderProps {
  className?: string;
}

/**
 * CampusSlider — "IBAS Campus Thalwil", an accessible image carousel of campus
 * scenes. One large stage shows the active slide, steered by real prev/next
 * <button>s, an arrow-key-aware dot tablist and a thumbnail strip; the active
 * index lives in React state so the active slide is always rendered visible.
 *
 * Motion intent: advancing crossfades the incoming slide up into place (GSAP,
 * opacity + a small rise) over the outgoing one, and a gentle auto-advance
 * keeps the stage alive — it pauses whenever the pointer or keyboard focus is
 * within the carousel. Under reduced motion the auto-advance is disabled
 * entirely and slides simply cut to their final visible state with no animated
 * transition; every control keeps working, so nothing is ever stranded hidden.
 */
export function CampusSlider({ className }: CampusSliderProps) {
  const reduced = useReducedMotion();
  const count = SLIDES.length;

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const stageRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const prevActive = useRef(0);

  const setSlideRef = useCallback((el: HTMLDivElement | null, i: number) => {
    slideRefs.current[i] = el;
  }, []);

  const go = useCallback(
    (i: number) => setActive(((i % count) + count) % count),
    [count],
  );

  // Crossfade the active slide in over the previous one. Explicit fromTo with a
  // visible destination so a slide can never strand hidden; under reduced motion
  // we simply set the active slide visible and the rest hidden — no animation.
  useGSAP(
    () => {
      const slides = slideRefs.current;
      const prev = prevActive.current;
      prevActive.current = active;

      if (reduced) {
        slides.forEach((el, i) => {
          if (el) gsap.set(el, { autoAlpha: i === active ? 1 : 0, yPercent: 0 });
        });
        return;
      }

      slides.forEach((el, i) => {
        if (!el) return;
        if (i === active) {
          gsap.fromTo(
            el,
            { autoAlpha: 0, yPercent: i === prev ? 0 : 4 },
            {
              autoAlpha: 1,
              yPercent: 0,
              duration: DURATION.slow,
              ease: GSAP_EASE.out,
            },
          );
        } else if (i === prev) {
          gsap.to(el, {
            autoAlpha: 0,
            duration: DURATION.base,
            ease: GSAP_EASE.out,
          });
        } else {
          gsap.set(el, { autoAlpha: 0, yPercent: 0 });
        }
      });
    },
    { scope: stageRef, dependencies: [active, reduced] },
  );

  // Gentle auto-advance — never under reduced motion, and pauses whenever the
  // pointer or keyboard focus rests inside the carousel.
  useEffect(() => {
    if (reduced || paused || count < 2) return;
    const id = window.setInterval(() => setActive((a) => (a + 1) % count), AUTO_ADVANCE_MS);
    return () => window.clearInterval(id);
  }, [reduced, paused, count]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        go(active - 1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        go(active + 1);
      }
    },
    [active, go],
  );

  return (
    <section
      aria-labelledby="campus-slider-heading"
      className={cn(
        'flex min-h-[100svh] flex-col justify-center overflow-hidden bg-background py-12 md:py-16',
        className,
      )}
    >
      <div className="mx-auto w-full max-w-6xl px-6">
        <header className="mb-6 max-w-2xl md:mb-8">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Life on campus
          </p>
          <SplitReveal
            as="h2"
            by="words"
            className="mt-3 font-display text-3xl leading-tight font-semibold text-foreground md:text-4xl"
          >
            IBAS Campus Thalwil
          </SplitReveal>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Study on-site at the IBAS campus in Thalwil, Switzerland.
          </p>
        </header>

        {/* Carousel */}
        <div
          className="group relative"
          role="region"
          aria-roledescription="carousel"
          aria-label="IBAS Campus Thalwil photo gallery"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocusCapture={() => setPaused(true)}
          onBlurCapture={() => setPaused(false)}
          onKeyDown={onKeyDown}
        >
          {/* Stage — all slides stacked; the active one is always visible. */}
          <div
            ref={stageRef}
            className="relative h-[clamp(280px,52vh,560px)] w-full overflow-hidden rounded-2xl bg-muted shadow-sm"
          >
            {SLIDES.map((slide, i) => {
              const isActive = i === active;
              return (
                <div
                  key={slide.image}
                  ref={(el) => setSlideRef(el, i)}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${i + 1} of ${count}`}
                  aria-hidden={!isActive}
                  className={cn(
                    'absolute inset-0',
                    // Static, reduced-motion-safe fallback: the active slide is
                    // visible from the start even before GSAP runs.
                    isActive ? 'opacity-100' : 'opacity-0',
                  )}
                >
                  <img
                    src={slide.image}
                    alt={slide.alt}
                    loading={i === 0 ? 'eager' : 'lazy'}
                    style={{ objectPosition: slide.position }}
                    className="h-full w-full object-cover"
                  />
                  {/* Navy scrim so the white caption stays legible. */}
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-brand-navy/70 via-brand-navy/10 to-transparent"
                  />
                  <p className="absolute bottom-4 left-4 font-label text-xs tracking-[0.18em] text-white uppercase sm:bottom-6 sm:left-6 sm:text-sm">
                    {slide.caption}
                  </p>
                </div>
              );
            })}

            {/* Prev / next — navy background, white text (brand button rule). */}
            <button
              type="button"
              onClick={() => go(active - 1)}
              aria-label="Previous slide"
              className="absolute top-1/2 left-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-brand-navy text-white shadow-lg transition-colors duration-300 ease-out hover:bg-brand-navy/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold sm:left-4"
            >
              <ChevronLeft className="size-5" aria-hidden strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={() => go(active + 1)}
              aria-label="Next slide"
              className="absolute top-1/2 right-3 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-brand-navy text-white shadow-lg transition-colors duration-300 ease-out hover:bg-brand-navy/85 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-gold sm:right-4"
            >
              <ChevronRight className="size-5" aria-hidden strokeWidth={1.75} />
            </button>
          </div>

          {/* Thumbnail strip doubling as a tablist of slide selectors. */}
          <div
            className="mt-4 flex items-center justify-center gap-2 sm:gap-3"
            role="tablist"
            aria-label="Choose a campus slide"
          >
            {SLIDES.map((slide, i) => {
              const isActive = i === active;
              return (
                <button
                  key={slide.image}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-label={`Slide ${i + 1} of ${count}: ${slide.caption}`}
                  onClick={() => go(i)}
                  className={cn(
                    'relative h-14 w-20 shrink-0 overflow-hidden rounded-lg border-2 transition-[border-color,opacity,transform] duration-300 ease-out focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring sm:h-16 sm:w-24',
                    isActive
                      ? 'border-brand-gold opacity-100'
                      : 'border-transparent opacity-55 hover:opacity-100',
                  )}
                >
                  <img
                    src={slide.image}
                    alt=""
                    aria-hidden
                    loading="lazy"
                    style={{ objectPosition: slide.position }}
                    className="h-full w-full object-cover"
                  />
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
